import {
  addAllValuesToField,
  addFieldsToGroup,
  addSelectedField,
  addSelectedFilter,
  addValuesToGroup,
  addValueToTree,
  changeConditionalType,
  clearSelectedFields,
  clearSelectedFilters,
  removeFieldFromTree,
  removeFieldsFromGroup,
  removeSelectedField,
  removeSelectedFilter,
  removeValueFromTree,
  removeValuesFromGroup,
} from 'actions/dashboardEdit';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  getFiltersSearchString,
  getMetricEditIndex,
  getNavParamsFromTree,
  getSelectedFields,
  getSelectedFilters,
  getTree,
} from 'selectors/dashboardEdit';
import { useSelector } from 'store';
import { Condition, DataNode, Operator, ValueNode } from 'utils/parserTree';
import { v4 } from 'uuid';
import { FiltersTreeContext } from './ExploreFiltersTreeContext';

const DashboardEditFiltersTreeContext = React.createContext<FiltersTreeContext>({
  tree: {
    op: Operator.AND,
    data: [],
    id: v4(),
  },
  treeParams: {},
  filtersSearchString: '',
  selectedValues: [],
  selectedFields: [],
  metricIndex: 1,

  handleAddValueToTree: () => {
    // do nothing default
  },
  handleReplaceValueInTree: () => {
    // do nothing default
  },
  handleRemoveValueFromTree: () => {
    // do nothing default
  },
  handleAddAllValuesToField: () => {
    // do nothing default
  },
  handleRemoveFieldFromTree: () => {
    // do nothing default
  },
  handleChangeConditionalType: () => {
    // do nothing default
  },
  handleAddSelectedFilter: () => {
    // do nothing default
  },
  handleRemoveSelectedFilter: () => {
    // do nothing default
  },
  handleAddSelectedField: () => {
    // do nothing default
  },
  handleRemoveSelectedField: () => {
    // do nothing default
  },
  handleClearSelectedFields: () => {
    // do nothing default
  },
  handleClearSelectedFilters: () => {
    // do nothing default
  },
  handleAddValuesToGroup: () => {
    // do nothing default
  },
  handleRemoveValuesFromGroup: () => {
    // do nothing default
  },
  handleAddFieldsToGroup: () => {
    // do nothing default
  },
  handleRemoveFieldsFromGroup: () => {
    // do nothing default
  },
});

export const DashboardEditFiltersTreeProvider: React.FC<{ propMetricIndex?: number }> = ({
  propMetricIndex,
  children,
}) => {
  const dispatch = useDispatch();

  const storeMetricIndex = useSelector(getMetricEditIndex);
  const metricIndex = propMetricIndex !== undefined ? propMetricIndex : storeMetricIndex;

  const tree = useSelector((state) => getTree(state, metricIndex || 0));
  const treeParams = useSelector(getNavParamsFromTree);
  const filtersSearchString = useSelector((state) =>
    getFiltersSearchString(state, metricIndex || 0)
  );
  const selectedFields = useSelector(getSelectedFields);
  const selectedValues = useSelector(getSelectedFilters);

  const handleAddValueToTree = (
    value: string,
    field: string,
    label: string,
    isSingleValue = false
  ): void => {
    dispatch(
      addValueToTree({
        value,
        field,
        label,
        isSingleValue,
        metricIndex,
      })
    );
  };

  const handleReplaceValueInTree = (): void => {
    // moot
  };

  const handleRemoveValueFromTree = (value: string, field?: string, fieldId?: string): void => {
    dispatch(removeValueFromTree({ value, field, fieldId, metricIndex }));
  };

  const handleAddAllValuesToField = (
    field: string,
    nodes: { value: string; label?: string }[]
  ): void => {
    dispatch(
      addAllValuesToField({
        field,
        nodes,
        metricIndex,
      })
    );
  };

  const handleRemoveFieldFromTree = (field: string, id?: string): void => {
    dispatch(removeFieldFromTree({ field, metricIndex, id }));
  };

  const handleChangeConditionalType = (node: DataNode, condition: Condition): void => {
    dispatch(changeConditionalType({ node, condition, metricIndex }));
  };

  const handleAddSelectedFilter = (node: ValueNode): void => {
    dispatch(addSelectedFilter({ node }));
  };

  const handleRemoveSelectedFilter = (node: ValueNode): void => {
    dispatch(removeSelectedFilter({ node }));
  };

  const handleAddSelectedField = (node: DataNode): void => {
    dispatch(addSelectedField({ node }));
  };

  const handleRemoveSelectedField = (node: DataNode): void => {
    dispatch(removeSelectedField({ node }));
  };

  const handleClearSelectedFields = (): void => {
    dispatch(clearSelectedFields());
  };

  const handleClearSelectedFilters = (): void => {
    dispatch(clearSelectedFilters());
  };

  const handleAddValuesToGroup = (nodes: ValueNode[]): void => {
    dispatch(addValuesToGroup({ nodes, metricIndex }));
  };

  const handleRemoveValuesFromGroup = (nodes: ValueNode[]): void => {
    dispatch(removeValuesFromGroup({ nodes, metricIndex }));
  };

  const handleAddFieldsToGroup = (nodes: DataNode[]): void => {
    dispatch(addFieldsToGroup({ nodes, metricIndex }));
  };

  const handleRemoveFieldsFromGroup = (nodes: DataNode[]): void => {
    dispatch(removeFieldsFromGroup({ nodes, metricIndex }));
  };

  const value = {
    tree,
    treeParams,
    filtersSearchString,
    selectedFields,
    selectedValues,
    metricIndex,

    handleAddValueToTree,
    handleReplaceValueInTree,
    handleRemoveValueFromTree,
    handleAddAllValuesToField,
    handleRemoveFieldFromTree,
    handleChangeConditionalType,
    handleAddSelectedFilter,
    handleRemoveSelectedFilter,
    handleAddSelectedField,
    handleRemoveSelectedField,
    handleClearSelectedFields,
    handleClearSelectedFilters,

    handleAddValuesToGroup,
    handleRemoveValuesFromGroup,
    handleAddFieldsToGroup,
    handleRemoveFieldsFromGroup,
  };

  return (
    <DashboardEditFiltersTreeContext.Provider value={value}>
      {children}
    </DashboardEditFiltersTreeContext.Provider>
  );
};

export default DashboardEditFiltersTreeContext;
